import { env } from 'cloudflare:workers';
import { createMiddleware, createStart } from '@tanstack/react-start';
import { applySecurityHeaders } from './lib/securityHeaders';

// TanStack Start's global request middleware. The plugin auto-discovers this
// file at `src/start.ts` (@tanstack/start-plugin-core planning.js:56, default
// entry "start", not required), so there is no config change; `wrangler.jsonc`
// keeps pointing `main` at the framework's own entry path, which the plugin now
// resolves to `src/server.ts` (see that file for why negotiation lives there).
//
// It exists to close one hole: `public/_headers` is applied by Cloudflare
// Workers Assets and therefore never reaches a response the worker GENERATES.
// Verified live: `/providers/<id>`, `/docs` and `/concierge/*` all returned
// zero security headers while the prerendered `/providers` index
// returned all five.

const securityHeaders = createMiddleware({ type: 'request' }).server(async ({ next }) => {
  const result = await next();
  // A response from `fetch()` has IMMUTABLE headers in Workers, and the docs
  // route returns exactly that. Rebuilding from the existing response is the
  // only way to add a header to one; passing `response.body` through keeps a
  // streaming response streaming rather than buffering.
  const response = new Response(result.response.body, result.response);
  return { ...result, response: applySecurityHeaders(response) };
});

/**
 * The prerendered file for this request, if Workers Assets holds one.
 *
 * `run_worker_first` (wrangler.jsonc) puts this middleware in front of the
 * marketing pages so `Accept: text/markdown` can be honoured on them, which
 * makes serving the prerendered bytes our job rather than the asset worker's.
 * Re-rendering them here instead would be the expensive mistake: the pages are
 * prerendered precisely so a request never costs a catalog fetch, and the
 * gateway rate-limits every SSR fetch from this Worker into one bucket.
 */
async function prerenderedAsset(request: Request): Promise<Response | undefined> {
  const assets = env.ASSETS;
  if (!assets) return undefined;
  if (request.method !== 'GET' && request.method !== 'HEAD') return undefined;
  // Ask for the HTML representation explicitly: the asset worker does not
  // negotiate, and forwarding `Accept: text/markdown` would only be confusing.
  const probe = new Request(request.url, {
    method: request.method,
    headers: { accept: 'text/html' },
  });
  const response = await assets.fetch(probe);
  return response.status === 404 ? undefined : response;
}

const prerenderedPages = createMiddleware({ type: 'request' }).server(async ({ request, next }) => {
  // A short-circuit `Response` is part of the request-middleware contract
  // (RequestMiddlewareServerFnResult), which is what lets the prerendered asset
  // answer without the router ever running. Negotiation happens above this, in
  // `src/server.ts`, which is the only place that sees the caller's real Accept.
  const asset = await prerenderedAsset(request);
  return asset ?? next();
});

export const startInstance = createStart(() => ({
  requestMiddleware: [securityHeaders, prerenderedPages],
}));
